/**
 * 2026-02 — SchadenDetail: Detail-Seite eines Schadens mit Tabs
 *   Übersicht · Dokumente · Verlauf · Vorgang
 * Beinhaltet den Button „Neues Schreiben" → öffnet den MessagingDialog mit
 * `context.damage_id`, wodurch das Backend „Unser Zeichen" automatisch auf die
 * Schadennummer setzt und das erzeugte Dokument der Schaden-Dokumentenmappe
 * zuordnet (`linked_damage_id`).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatDate } from "@/lib/constants";
import MessagingDialog from "@/components/MessagingDialog";
import {
  ArrowLeft, PaperPlaneTilt, FileText, Clock, Wrench, Briefcase, CheckCircle, Warning,
} from "@phosphor-icons/react";

const DAMAGE_STATUS = {
  reported: { label: "Gemeldet", cls: "bg-amber-100 text-amber-800" },
  in_progress: { label: "In Bearbeitung", cls: "bg-blue-100 text-blue-800" },
  contractor_assigned: { label: "Handwerker beauftragt", cls: "bg-indigo-100 text-indigo-800" },
  fixed: { label: "Behoben", cls: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Abgelehnt", cls: "bg-slate-200 text-slate-700" },
  closed: { label: "Abgeschlossen", cls: "bg-slate-100 text-slate-600" },
};

const DAMAGE_PRIORITY = {
  low: { label: "Niedrig", cls: "text-slate-600" },
  medium: { label: "Mittel", cls: "text-amber-700" },
  high: { label: "Hoch", cls: "text-orange-700" },
  critical: { label: "Kritisch", cls: "text-rose-700 font-semibold" },
};

export default function SchadenDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [damage, setDamage] = useState(null);
  const [property, setProperty] = useState(null);
  const [unit, setUnit] = useState(null);
  const [tenancy, setTenancy] = useState(null);
  const [reporter, setReporter] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msgOpen, setMsgOpen] = useState(false);
  const [busyConvert, setBusyConvert] = useState(false);
  const [ticketAttachments, setTicketAttachments] = useState([]);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/damages/${id}`);
      setDamage(data);
      // Zusatz-Kontext parallel laden
      const jobs = [];
      if (data.property_id) jobs.push(api.get(`/properties/${data.property_id}`).then(({ data }) => setProperty(data)).catch(() => setProperty(null)));
      if (data.unit_id) jobs.push(api.get("/properties/units/all", { params: { property_id: data.property_id } }).then(({ data: us }) => {
        const u = (us || []).find((x) => x.id === data.unit_id) || null;
        setUnit(u);
      }).catch(() => setUnit(null)));
      if (data.tenancy_id) jobs.push(api.get(`/tenancies/${data.tenancy_id}`).then(({ data }) => setTenancy(data)).catch(() => setTenancy(null)));
      if (data.reporter_contact_id) jobs.push(api.get(`/contacts/${data.reporter_contact_id}`).then(({ data }) => setReporter(data)).catch(() => setReporter(null)));
      if (data.assigned_supplier_contact_id) jobs.push(api.get(`/contacts/${data.assigned_supplier_contact_id}`).then(({ data }) => setSupplier(data)).catch(() => setSupplier(null)));
      if (data.ticket_id) jobs.push(api.get(`/tickets/${data.ticket_id}`).then(({ data }) => setTicket(data)).catch(() => setTicket(null)));
      await Promise.allSettled(jobs);
      // Dokumente separat
      try {
        const { data: docsRes } = await api.get(`/damages/${id}/documents`);
        setDocuments(docsRes.items || []);
      } catch { setDocuments([]); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Schaden konnte nicht geladen werden");
      navigate("/schaeden");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const changeStatus = async (newStatus) => {
    try {
      await api.patch(`/damages/${id}`, { status: newStatus });
      toast.success("Status aktualisiert");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Statuswechsel fehlgeschlagen");
    }
  };

  const convertToVorgang = async () => {
    setBusyConvert(true);
    try {
      const { data } = await api.post(`/damages/${id}/convert-to-vorgang`, {});
      toast.success(`Vorgang ${data.case_number} angelegt — Dokumente wurden mit übernommen`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Konvertierung fehlgeschlagen");
    } finally { setBusyConvert(false); }
  };

  const openLetter = () => {
    // ticket_attachments = Bilder aus dem Auto-Ticket bereitstellen, damit
    // der Composer sie als E-Mail-Anhang anbieten kann.
    setTicketAttachments(ticket?.attachments || []);
    setMsgOpen(true);
  };

  const messagingContext = useMemo(() => {
    if (!damage) return null;
    return {
      damage_id: damage.id,
      // Zusatz-Bindungen mitgeben, damit die Empfänger-/Adress-Logik greift.
      // Priorität: MV > Meldender > Objekt.
      tenancy_id: damage.tenancy_id || undefined,
      contact_id: !damage.tenancy_id ? (damage.reporter_contact_id || undefined) : undefined,
      property_id: damage.property_id || undefined,
      unit_id: damage.unit_id || undefined,
    };
  }, [damage]);

  if (loading || !damage) {
    return <div className="p-6 text-sm text-slate-500">Lade Schaden…</div>;
  }
  const s = DAMAGE_STATUS[damage.status] || DAMAGE_STATUS.reported;
  const p = DAMAGE_PRIORITY[damage.priority] || DAMAGE_PRIORITY.medium;
  const ticketClosed = ticket && ["resolved", "closed"].includes(ticket.status);
  const canMarkFixed = ticketClosed;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <button
            onClick={() => navigate("/schaeden")}
            className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1"
            data-testid="back-to-schaeden"
          >
            <ArrowLeft size={12} /> Zurück zur Liste
          </button>
          <h1 className="text-2xl font-bold text-slate-900 mt-1 flex items-center gap-3">
            <span className="tabular-nums">{damage.damage_number}</span>
            <span className={`text-xs ${p.cls}`}>{p.label}</span>
          </h1>
          <div className="text-sm text-slate-700 mt-1">{damage.title}</div>
        </div>
        <div className="flex gap-2">
          <Select value={damage.status} onValueChange={changeStatus}>
            <SelectTrigger className="h-9 w-52" data-testid="damage-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(DAMAGE_STATUS).map(([k, v]) => (
                <SelectItem
                  key={k} value={k}
                  disabled={k === "fixed" && !canMarkFixed}
                >
                  {v.label}{k === "fixed" && !canMarkFixed ? " (Vorgang muss geschlossen sein)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={openLetter} data-testid="damage-new-letter">
            <PaperPlaneTilt size={14} className="mr-1" /> Neues Schreiben
          </Button>
          {!damage.vorgang_id && (
            <Button variant="outline" onClick={convertToVorgang} disabled={busyConvert} data-testid="damage-convert-vorgang">
              <Briefcase size={14} className="mr-1" />
              {busyConvert ? "Konvertiere…" : "→ In Vorgang überführen"}
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Übersicht</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Dokumente ({documents.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Verlauf ({(damage.history || []).length})</TabsTrigger>
          <TabsTrigger value="vorgang" data-testid="tab-vorgang">Vorgang</TabsTrigger>
        </TabsList>

        {/* --------- Übersicht --------- */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-md p-4">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Objekt / Einheit
              </div>
              <div className="text-sm text-slate-800">
                <div className="font-medium">
                  {property ? (property.name || property.property_number) : "—"}
                </div>
                {property?.address && (
                  <div className="text-xs text-slate-500">
                    {[property.address.street, property.address.house_number].filter(Boolean).join(" ")},{" "}
                    {[property.address.zip, property.address.city].filter(Boolean).join(" ")}
                  </div>
                )}
                {unit && (
                  <div className="text-xs text-slate-600 mt-2">
                    Einheit <span className="font-medium">{unit.unit_number}</span>
                    {unit.floor_label && <span className="text-slate-400"> · {unit.floor_label}</span>}
                    {unit.living_area && <span className="text-slate-400"> · {unit.living_area} m²</span>}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-md p-4">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Meldender / Beteiligte
              </div>
              <div className="text-sm space-y-2">
                <div>
                  <div className="text-xs text-slate-500">Meldender</div>
                  <div className="text-slate-800">
                    {reporter
                      ? (reporter.company_name || `${reporter.first_name || ""} ${reporter.last_name || ""}`.trim())
                      : "—"}
                    {reporter?.emails?.[0] && (
                      <span className="text-xs text-slate-500 ml-2">
                        {typeof reporter.emails[0] === "string" ? reporter.emails[0] : reporter.emails[0].email}
                      </span>
                    )}
                  </div>
                </div>
                {supplier && (
                  <div>
                    <div className="text-xs text-slate-500 flex items-center gap-1"><Wrench size={11} /> Handwerker</div>
                    <div className="text-slate-800">
                      {supplier.company_name || `${supplier.first_name || ""} ${supplier.last_name || ""}`.trim()}
                    </div>
                  </div>
                )}
                {tenancy && (
                  <div>
                    <div className="text-xs text-slate-500">Mietverhältnis</div>
                    <div className="text-slate-800 text-xs">
                      {tenancy.contract_number || tenancy.tenancy_number}
                      {" · "}
                      {(tenancy.tenant_names || []).join(", ")}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
              Beschreibung
            </div>
            <div className="text-sm text-slate-800 whitespace-pre-wrap">
              {damage.description || <span className="text-slate-400 italic">Keine Beschreibung</span>}
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div>
                <div className="text-slate-500">Schadendatum</div>
                <div className="font-medium tabular-nums">{formatDate(damage.occurred_at) || "—"}</div>
              </div>
              <div>
                <div className="text-slate-500">Behoben am</div>
                <div className="font-medium tabular-nums">{formatDate(damage.fixed_at) || "—"}</div>
              </div>
              <div>
                <div className="text-slate-500">Mieter-Haftung</div>
                <div className="font-medium">{damage.tenant_liable ? "Ja" : "Nein"}</div>
              </div>
              <div>
                <div className="text-slate-500">Vers.-Fall-Nr.</div>
                <div className="font-medium">{damage.insurance_case_number || "—"}</div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* --------- Dokumente --------- */}
        <TabsContent value="documents" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-md">
            {documents.length === 0 && (
              <div className="p-8 text-center">
                <FileText size={32} className="mx-auto text-slate-400 mb-2" />
                <div className="text-sm text-slate-600">Noch keine Dokumente in der Mappe.</div>
                <div className="text-xs text-slate-500 mt-1">Über „Neues Schreiben" wird das erste Dokument erzeugt.</div>
              </div>
            )}
            {documents.length > 0 && (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left">
                  <tr>
                    <th className="px-3 py-2 text-xs font-semibold text-slate-600">Titel</th>
                    <th className="px-3 py-2 text-xs font-semibold text-slate-600">Kategorie</th>
                    <th className="px-3 py-2 text-xs font-semibold text-slate-600">Erstellt</th>
                    <th className="px-3 py-2 text-xs font-semibold text-slate-600">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((d) => (
                    <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2 text-slate-800">{d.title || d.original_filename}</td>
                      <td className="px-3 py-2 text-xs text-slate-600">{d.category || "—"}</td>
                      <td className="px-3 py-2 text-xs text-slate-600 tabular-nums">{formatDate(d.created_at)}</td>
                      <td className="px-3 py-2">
                        <a
                          href={`/api/documents/${d.id}/download`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-teal-700 hover:underline"
                          data-testid={`doc-open-${d.id}`}
                        >
                          Öffnen
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        {/* --------- Verlauf --------- */}
        <TabsContent value="history" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
            {(damage.history || []).length === 0 && (
              <div className="text-sm text-slate-500">Kein Verlaufseintrag.</div>
            )}
            {(damage.history || []).slice().reverse().map((h, i) => (
              <div key={i} className="flex gap-3 items-start text-sm">
                <Clock size={14} className="text-slate-400 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="text-slate-800">{h.note || h.event}</div>
                  <div className="text-xs text-slate-500 tabular-nums">{formatDate(h.at)}</div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* --------- Vorgang --------- */}
        <TabsContent value="vorgang" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-md p-4">
            {damage.vorgang_id ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle size={22} className="text-emerald-600" />
                  <div>
                    <div className="text-sm font-medium">Schaden wurde in einen Vorgang überführt.</div>
                    <div className="text-xs text-slate-500">Alle Dokumente sind auch dem Vorgang zugeordnet.</div>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate(`/vorgaenge?highlight=${damage.vorgang_id}`)}>
                  Vorgang öffnen
                </Button>
              </div>
            ) : ticket ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Briefcase size={14} className="text-slate-500" />
                  <span className="text-slate-700">Auto-Ticket:</span>
                  <span className="font-mono">{ticket.ticket_number}</span>
                  <Badge>{ticket.status}</Badge>
                </div>
                <div className="text-xs text-slate-600">
                  Für erweiterte Bearbeitung, Beteiligte und Timeline: Schaden in Vorgang überführen. Bestehende Dokumente
                  werden dabei übernommen und bleiben erhalten.
                </div>
                <Button size="sm" onClick={convertToVorgang} disabled={busyConvert} data-testid="convert-vorgang-tab">
                  <Briefcase size={14} className="mr-1" />
                  {busyConvert ? "Konvertiere…" : "In Vorgang überführen"}
                </Button>
                <div className="text-xs text-amber-700 flex items-center gap-2 mt-2">
                  <Warning size={12} /> Der Schaden kann erst als „behoben" markiert werden, wenn der Vorgang abgeschlossen ist.
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-500">Kein verknüpfter Vorgang.</div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {msgOpen && messagingContext && (
        <MessagingDialog
          open={msgOpen}
          onOpenChange={(o) => { setMsgOpen(o); if (!o) load(); }}
          context={messagingContext}
          defaultSubject={`Schaden ${damage.damage_number}: ${damage.title}`}
        />
      )}
    </div>
  );
}
